import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import { useTheme } from '@mui/material/styles';

import { ControlButton } from '../controls';
import ChartShareDialog from './ChartShareDialog';
import {
  CHART_SHARE_LABEL_OPTIONS,
  ChartShareControlSet,
} from './ChartShareControls';
import { formatChartExportFilters } from './chartExportFilters';
import {
  CHART_EXPORT_MODES,
  getChartExportMode,
  validateChartExportConfig,
} from './chartExportMode';

const toChartId = (value) => String(value || 'analytics-chart')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '') || 'analytics-chart';

const hasChartGraphic = (node) => Boolean(node?.querySelector?.('svg, canvas'));

const normalizeText = (value) => String(value || '').replace(/\s+/g, ' ').trim();

const uniqueValues = (values) => [...new Set(values.map(normalizeText).filter(Boolean))];

const RESPONSIVE_NIVO_COMPONENT = /^Responsive(?:Bar|BoxPlot|Line|Marimekko|ScatterPlot|TreeMap)$/;

const getElementTypeName = (type) => type?.displayName
  || type?.name
  || type?.render?.displayName
  || type?.render?.name
  || '';

/** Disable animation and interaction on Nivo elements before mounting them in an export frame. */
export const getChartSeriesOptions = (node, result = new Map()) => {
  React.Children.forEach(node, (child) => {
    if (!React.isValidElement(child)) return;
    const data = child.props?.data;
    if (Array.isArray(data) && data.some((series) => series && Array.isArray(series.data) && series.id != null)) {
      data.forEach((series) => {
        if (series && Array.isArray(series.data) && series.id != null) {
          result.set(String(series.id), { value: series.id, label: String(series.id) });
        }
      });
    }
    if (Array.isArray(child.props?.keys)) {
      child.props.keys.forEach((key) => result.set(String(key), { value: key, label: String(key) }));
    }
    getChartSeriesOptions(child.props?.children, result);
  });
  return [...result.values()];
};

/** Apply export presentation and dialog-local series choices to a chart subtree. */
export const prepareChartForExport = (
  node,
  chartState = {},
  { filterSeries = true } = {}
) => React.Children.map(node, (child) => {
  if (!React.isValidElement(child)) return child;
  const children = child.props?.children
    ? prepareChartForExport(child.props.children, chartState, { filterSeries })
    : child.props?.children;
  const componentName = getElementTypeName(child.type);
  const selectedSeries = new Set(chartState.selectedSeries || []);
  const hasSeriesSelection = selectedSeries.size > 0;
  const data = child.props?.data;
  const seriesProps = filterSeries
    && hasSeriesSelection
    && Array.isArray(data)
    && data.some((series) => series && Array.isArray(series.data) && series.id != null)
    ? { data: data.filter((series) => selectedSeries.has(series.id) || selectedSeries.has(String(series.id))) }
    : {};
  const keyProps = filterSeries && hasSeriesSelection && Array.isArray(child.props?.keys)
    ? { keys: child.props.keys.filter((key) => selectedSeries.has(key) || selectedSeries.has(String(key))) }
    : {};
  const legendProps = filterSeries
    && hasSeriesSelection
    && /ChartLegend(?:Layout)?/.test(componentName)
    && Array.isArray(child.props?.items)
    ? {
      items: child.props.items.filter((item) => (
        selectedSeries.has(item?.id)
        || selectedSeries.has(String(item?.id))
      )),
    }
    : {};
  const labelProps = /Bar$/.test(componentName) && chartState.labelMode
    ? { enableLabel: child.props.enableLabel === false ? false : chartState.labelMode !== 'off' }
    : {};
  const staticProps = RESPONSIVE_NIVO_COMPONENT.test(componentName) ? {
    animate: false,
    isInteractive: false,
    enableCrosshair: false,
    useMesh: false,
  } : {};
  return React.cloneElement(child, {
    ...seriesProps,
    ...keyProps,
    ...legendProps,
    ...labelProps,
    ...staticProps,
  }, children);
});

function AutomaticChartShareControls({ chartState, setChartState, seriesOptions }) {
  return (
    <ChartShareControlSet
      chartState={chartState}
      setChartState={setChartState}
      controls={[
        ...(seriesOptions.length > 1 ? [{
          type: 'multiSelect',
          id: 'chart-share-series',
          stateKey: 'selectedSeries',
          label: 'Series',
          options: seriesOptions,
        }] : []),
        {
          type: 'toggle',
          id: 'chart-share-labels',
          stateKey: 'labelMode',
          label: 'Value labels',
          ariaLabel: 'Export value labels',
          options: CHART_SHARE_LABEL_OPTIONS,
        },
      ]}
    />
  );
}

const getSvgSize = (svg, dimension) => {
  const attributeValue = Number(svg.getAttribute(dimension));
  if (Number.isFinite(attributeValue) && attributeValue > 0) return attributeValue;

  const viewBox = svg.getAttribute('viewBox')?.trim().split(/[\s,]+/).map(Number);
  const viewBoxValue = viewBox?.[dimension === 'width' ? 2 : 3];
  if (Number.isFinite(viewBoxValue) && viewBoxValue > 0) return viewBoxValue;

  const rectValue = svg.getBoundingClientRect?.()[dimension];
  return Number.isFinite(rectValue) && rectValue > 0 ? rectValue : null;
};

/**
 * Capture application-owned chart markup while making fixed-size Nivo SVGs
 * safely scalable inside every image preset. Nivo emits width/height without a
 * viewBox, so changing only its CSS dimensions clips the original coordinate
 * system instead of scaling it.
 */
export const captureChartMarkup = (node) => {
  if (!node) return '';
  const clone = node.cloneNode(true);
  const sourceSvgs = [...node.querySelectorAll('svg')];
  const clonedSvgs = [...clone.querySelectorAll('svg')];

  clonedSvgs.forEach((svg, index) => {
    const source = sourceSvgs[index];
    if (!source) return;
    const width = getSvgSize(source, 'width');
    const height = getSvgSize(source, 'height');
    if (!svg.hasAttribute('viewBox') && width && height) {
      svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
    }
    svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    svg.setAttribute('width', '100%');
    svg.setAttribute('height', '100%');
    svg.style.width = '100%';
    svg.style.height = '100%';
    svg.style.maxWidth = '100%';
    svg.style.maxHeight = '100%';
  });

  const sourceCanvases = [...node.querySelectorAll('canvas')];
  const clonedCanvases = [...clone.querySelectorAll('canvas')];
  clonedCanvases.forEach((canvas, index) => {
    const source = sourceCanvases[index];
    if (!source?.toDataURL) return;
    try {
      const image = document.createElement('img');
      image.src = source.toDataURL('image/png');
      image.alt = source.getAttribute('aria-label') || 'Chart';
      image.style.cssText = 'display:block;width:100%;height:100%;object-fit:contain;';
      canvas.replaceWith(image);
    } catch (error) {
      // A cross-origin canvas cannot be serialized. Leave the cloned canvas in
      // place so the export can still fail through the standard error surface.
    }
  });

  return clone.innerHTML;
};

const getControlValues = (control, sublabel) => {
  const candidates = [];
  control.querySelectorAll('input').forEach((input) => {
    if (input.type !== 'hidden') candidates.push(input.value);
  });
  control.querySelectorAll('[aria-pressed="true"], [aria-selected="true"], .MuiChip-label, .MuiSelect-select')
    .forEach((element) => candidates.push(element.textContent));
  control.querySelectorAll('[role="combobox"]').forEach((element) => {
    candidates.push(element.value || element.textContent || element.getAttribute('aria-label'));
  });

  const resolved = uniqueValues(candidates).filter((value) => value !== sublabel);
  if (resolved.length) {
    const compact = resolved.length > 3
      ? [...resolved.slice(0, 3), `+${resolved.length - 3} more`]
      : resolved;
    return compact.join(', ');
  }

  const fullText = normalizeText(control.textContent);
  return normalizeText(fullText.replace(sublabel, ''));
};

/** Read the live workspace filter UI so a generic chart export retains context. */
export const captureWorkspaceFilterLabels = (root = document) => {
  const hosts = [...(root?.querySelectorAll?.('[data-testid="workspace-filter-host"]') || [])]
    .filter((host) => host.getAttribute('aria-hidden') !== 'true');
  const host = hosts[hosts.length - 1];
  if (!host) return [];

  try {
    const hostFilters = JSON.parse(host.getAttribute('data-chart-export-filters') || '[]');
    const contentFilters = JSON.parse(
      host.querySelector('[data-chart-export-filters]')?.getAttribute('data-chart-export-filters') || '[]'
    );
    const structuredFilters = hostFilters.length ? hostFilters : contentFilters;
    const structuredLabels = formatChartExportFilters(structuredFilters);
    if (structuredLabels.length) return structuredLabels;
  } catch (error) {
    // Fall through to the compatibility path for legacy filter portals.
  }

  const content = host.querySelector('[data-workspace-filter-content="true"]');
  const labels = content ? [...content.children].map((control) => {
    if (control.getAttribute('aria-hidden') === 'true' || control.matches('.MuiDivider-root')) return '';
    const sublabel = normalizeText(control.querySelector('[data-filter-sublabel="true"]')?.textContent);
    const value = getControlValues(control, sublabel);
    if (sublabel && value) return `${sublabel}: ${value}`;
    return value || sublabel;
  }) : [];

  const resolved = uniqueValues(labels);
  if (resolved.length) return resolved;
  try {
    return uniqueValues(JSON.parse(host.getAttribute('data-chart-export-filter-labels') || '[]'));
  } catch (error) {
    return [];
  }
};

function StaticChartSnapshot({ html }) {
  return (
    <Box
      data-testid="chart-export-snapshot"
      sx={{
        width: '100%',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'stretch',
        '& > *': {
          flex: '1 1 auto',
          width: '100% !important',
          height: '100% !important',
          maxWidth: '100% !important',
          minWidth: '0 !important',
          minHeight: '0 !important',
        },
        '& svg, & canvas, & img': {
          display: 'block',
          width: '100% !important',
          height: '100% !important',
          maxWidth: '100% !important',
          maxHeight: '100% !important',
          objectFit: 'contain',
        },
      }}
      // The markup is captured from an application-owned chart subtree. Event
      // handlers are not serialized, so the preview is a non-interactive copy.
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

function ChartPanelExport({
  frameRef,
  title,
  description,
  enabled = true,
  exportConfig,
  renderChart,
  chartNode,
}) {
  const theme = useTheme();
  const config = exportConfig && typeof exportConfig === 'object' ? exportConfig : {};
  const exportMode = getChartExportMode(exportConfig);
  const configError = validateChartExportConfig(exportConfig);
  const entity = config.entity || { kind: 'product', label: config.entityLabel };
  const selectionEntity = ['company', 'package', 'project'].includes(entity.kind);
  const includeWorkspaceFilters = config.filterMode === 'workspace'
    || (config.filterMode !== 'none' && !selectionEntity);
  const [open, setOpen] = useState(false);
  const [snapshotHtml, setSnapshotHtml] = useState('');
  const [activeFilterLabels, setActiveFilterLabels] = useState([]);
  const domainRenderer = typeof config.renderChart === 'function';
  const automaticInteractive = config.interactiveControls === 'auto';
  const liveRenderer = config.renderChart || renderChart;
  const hasLiveRenderer = typeof liveRenderer === 'function';
  const [graphicReady, setGraphicReady] = useState(hasLiveRenderer);

  useEffect(() => {
    if (hasLiveRenderer) {
      setGraphicReady(true);
      return undefined;
    }
    const node = frameRef?.current;
    if (!node) {
      setGraphicReady(false);
      return undefined;
    }
    const updateReady = () => setGraphicReady(hasChartGraphic(node));
    updateReady();
    if (typeof MutationObserver === 'undefined') return undefined;
    const observer = new MutationObserver(updateReady);
    observer.observe(node, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [frameRef, hasLiveRenderer]);

  const handleOpen = useCallback(() => {
    if (!enabled || !graphicReady || configError) return;
    if (!hasLiveRenderer) {
      const node = frameRef?.current;
      if (!node || !hasChartGraphic(node)) return;
      setSnapshotHtml(captureChartMarkup(node));
    }
    setActiveFilterLabels(includeWorkspaceFilters ? captureWorkspaceFilterLabels() : []);
    setOpen(true);
  }, [configError, enabled, frameRef, graphicReady, hasLiveRenderer, includeWorkspaceFilters]);

  const renderSnapshot = useCallback(() => (
    <StaticChartSnapshot html={snapshotHtml} />
  ), [snapshotHtml]);
  const renderLiveChart = useCallback((renderArgs) => (
    prepareChartForExport(liveRenderer?.(renderArgs), renderArgs.chartState, {
      // Domain renderers already interpret their own selection state. Their
      // stable control values do not necessarily match the rendered series
      // labels, so applying the automatic filter again can erase the chart.
      filterSeries: automaticInteractive,
    })
  ), [automaticInteractive, liveRenderer]);
  const seriesOptions = useMemo(() => getChartSeriesOptions(chartNode), [chartNode]);
  const automaticInitialState = useMemo(() => ({
    selectedSeries: seriesOptions.map((option) => option.value),
    labelMode: 'auto',
  }), [seriesOptions]);
  const initialChartState = useMemo(() => (
    config.initialChartState || (automaticInteractive ? automaticInitialState : {})
  ), [automaticInitialState, automaticInteractive, config.initialChartState]);
  const renderChartControls = config.renderChartControls || (automaticInteractive
    ? ({ chartState, setChartState }) => (
        <AutomaticChartShareControls chartState={chartState} setChartState={setChartState} seriesOptions={seriesOptions} />
      )
    : undefined);
  const resolvedTitle = config.initialTitle || title || 'Analytics Chart';
  const resolvedSubtitle = config.initialSubtitle ?? description ?? '';
  const hasExplicitExportFilters = Array.isArray(config.exportFilters);
  const configuredFilterLabels = hasExplicitExportFilters
    ? formatChartExportFilters(config.exportFilters)
    : !includeWorkspaceFilters ? [] : config.activeFilterLabels;

  return (
    <>
      <ControlButton
        context="objectToolbar"
        variant="icon"
        icon={<ShareOutlinedIcon />}
        tooltip="Preview and download chart image"
        ariaLabel={`Share ${title || 'chart'} image`}
        onClick={handleOpen}
        disabled={!enabled || !graphicReady || Boolean(configError)}
        data-chart-export-action
        data-chart-export-mode={exportMode}
      />
      <ChartShareDialog
        open={open}
        onClose={() => setOpen(false)}
        chartId={config.chartId || toChartId(title)}
        ticker={config.ticker || entity.ticker}
        companyName={config.companyName}
        entity={entity}
        buildEntity={config.buildEntity}
        initialTitle={resolvedTitle}
        initialSubtitle={resolvedSubtitle}
        sourceLabel={config.sourceLabel || 'SoftwareIQ'}
        metadata={config.metadata}
        buildMetadata={config.buildMetadata}
        initialChartState={initialChartState}
        renderChartControls={renderChartControls}
        renderChart={hasLiveRenderer ? renderLiveChart : renderSnapshot}
        initialThemeMode={config.initialThemeMode || theme.palette.mode}
        allowThemeSelection={config.allowThemeSelection
          ?? (domainRenderer || exportMode === CHART_EXPORT_MODES.INTERACTIVE)}
        activeFilterLabels={configuredFilterLabels || activeFilterLabels}
      />
    </>
  );
}

export default ChartPanelExport;
